import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../../i18n/LanguageContext";
import { SparkoLogo } from "../SparkoLogo";
import { Icon } from "../common";
import { CONTACT, whatsappLink } from "../../data/site";

const NAV = [
  ["home", "/"], ["about", "/about"], ["services", "/services"], ["industries", "/industries"],
  ["projects", "/projects"], ["gallery", "/gallery"], ["reviews", "/reviews"], ["blog", "/blog"], ["contact", "/contact"],
];

export const Header = () => {
  const { t, lang, setLang, langNames } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setLangOpen(false); }, [loc.pathname]);

  const dark = !scrolled && loc.pathname === "/";
  const textCol = dark ? "text-white" : "text-navy";

  return (
    <motion.header
      initial={{ y: -80 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,padding] duration-500 ${scrolled ? "glass py-3 shadow-sm" : "py-5 bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" aria-label="Sparko home">
          <SparkoLogo dark={!dark} size={26} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map(([key, path]) => (
            <Link
              key={key} to={path} data-testid={`nav-${key}`}
              className={`text-sm font-medium link-underline ${loc.pathname === path ? "text-blue" : textCol} hover:opacity-90`}
            >
              {t.nav[key]}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)} data-testid="lang-switcher"
              className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium ${dark ? "border-white/25 text-white" : "border-black/10 text-navy"} hover:border-blue transition-colors`}
            >
              <Icon name="Globe" size={16} /> <span className="uppercase">{lang}</span>
              <Icon name="ChevronDown" size={14} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  className="absolute end-0 mt-2 w-40 rounded-2xl glass p-2 shadow-xl"
                >
                  {Object.keys(langNames).map((l) => (
                    <button
                      key={l} onClick={() => { setLang(l); setLangOpen(false); }} data-testid={`lang-${l}`}
                      className={`w-full text-start rounded-xl px-3 py-2 text-sm hover:bg-blue/10 ${lang === l ? "text-blue font-semibold" : "text-navy"}`}
                    >
                      {langNames[l]}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button onClick={() => nav("/quote")} data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-blue px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0052FF] transition-colors">
            {t.cta.quote} <Icon name="ArrowRight" size={16} className="rtl:rotate-180" />
          </button>

          <button className={`lg:hidden ${textCol}`} onClick={() => setOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
            <Icon name="Menu" size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-navy/95 backdrop-blur-xl lg:hidden" data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5">
              <SparkoLogo dark={false} size={26} />
              <button onClick={() => setOpen(false)} className="text-white" aria-label="Close menu"><Icon name="X" size={28} /></button>
            </div>
            <nav className="flex flex-col px-6 pt-6 gap-1">
              {NAV.concat([["faq", "/faq"]]).map(([key, path], i) => (
                <motion.div key={key} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                  <Link to={path} className="block py-3 text-2xl font-display font-bold text-white/90 hover:text-blue">{t.nav[key] || key}</Link>
                </motion.div>
              ))}
              <Link to="/quote" className="mt-6 inline-flex justify-center rounded-full bg-blue px-6 py-4 text-lg font-semibold text-white">{t.cta.quoteFree}</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
